import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/api";

const toInt = (v) => {
  if (v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

export default function ReturnBatch() {
  const { batchId } = useParams();
  const navigate = useNavigate();

  const [batch, setBatch] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [tempMode, setTempMode] = useState("resolve"); // resolve | partial

  useEffect(() => {
    const fetchBatch = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await api.get(`/admin/borrow-batches/${batchId}/approval/`);
        setBatch(res.data);

        const prepared = (res.data.items || []).map((bi) => {
          const returned = Number(bi.returned_total || 0);
          const remaining =
            bi.remaining !== undefined && bi.remaining !== null
              ? Number(bi.remaining)
              : Math.max(Number(bi.quantity || 0) - returned, 0);

          return {
            batch_item_id: bi.id,
            item_id: bi.item_id,
            item_code: bi.item_code,
            item_name: bi.item_name,
            is_temporary: ["GIMMICK", "BOOTH"].includes(bi.item_type),

            borrowed: Number(bi.quantity || 0),
            returned,
            remaining: Number.isFinite(remaining) ? remaining : 0,

            // normal item fields
            good_qty: "",
            damaged_qty: "",
            damaged_note: "",

            // gimmick item fields
            returned_to_stock: "",
            permanently_out: "",
          };
        });

        setItems(prepared);
      } catch (err) {
        console.error(err);
        setError("Failed to load batch.");
      } finally {
        setLoading(false);
      }
    };

    fetchBatch();
  }, [batchId]);

  const updateItem = (index, field, value) => {
    setItems((prev) => {
      const copy = [...prev];
      const row = { ...copy[index] };

      if (
        field === "good_qty" ||
        field === "damaged_qty" ||
        field === "returned_to_stock" ||
        field === "permanently_out"
      ) {
        if (value === "" || /^\d+$/.test(value)) {
          row[field] = value;
        }
      } else if (field === "damaged_note") {
        row.damaged_note = value;
      }

      copy[index] = row;
      return copy;
    });
  };

  const normalItems = useMemo(
    () => items.filter((it) => !it.is_temporary),
    [items]
  );

  const tempItems = useMemo(
    () => items.filter((it) => it.is_temporary),
    [items]
  );

  const exceedsRemainingNormal = (it) =>
    toInt(it.good_qty) + toInt(it.damaged_qty) > it.remaining;

  const missingDamagedNote = (it) =>
    toInt(it.damaged_qty) > 0 && !it.damaged_note.trim();

  const isNormalRowInvalid = (it) =>
    exceedsRemainingNormal(it) || missingDamagedNote(it);

  const normalRowErrorMessage = (it) => {
    if (exceedsRemainingNormal(it)) {
      return `Total return exceeds remaining (${it.remaining}).`;
    }
    if (missingDamagedNote(it)) {
      return "Note is required when damaged quantity is greater than 0.";
    }
    return "";
  };

  const exceedsRemainingTemporary = (it) => {
    if (tempMode === "resolve") {
      return toInt(it.returned_to_stock) > it.remaining;
    }
    return toInt(it.returned_to_stock) + toInt(it.permanently_out) > it.remaining;
  };

  const computedPermanentOut = (it) => {
    if (tempMode === "resolve") {
      const val = it.remaining - toInt(it.returned_to_stock);
      return val >= 0 ? val : 0;
    }
    return toInt(it.permanently_out);
  };

  const gimmickRowErrorMessage = (it) => {
    if (tempMode === "resolve") {
      if (exceedsRemainingTemporary(it)) {
        return `Returned to stock cannot exceed remaining (${it.remaining}).`;
      }
      return "";
    }

    if (exceedsRemainingTemporary(it)) {
      return `Returned to stock + permanently out cannot exceed remaining (${it.remaining}).`;
    }
    return "";
  };

  const hasAnyNormalReturn = normalItems.some(
    (it) => toInt(it.good_qty) + toInt(it.damaged_qty) > 0
  );

  const hasAnyGimmickReturn = tempItems.some((it) => {
    if (tempMode === "resolve") {
      return !exceedsRemainingTemporary(it) && it.remaining > 0 && it.returned_to_stock !== "";
    }
    return toInt(it.returned_to_stock) + toInt(it.permanently_out) > 0;
  });

  const hasAnyReturn = hasAnyNormalReturn || hasAnyGimmickReturn;

  const normalInvalid = normalItems.some(isNormalRowInvalid);
  const gimmickInvalid = tempItems.some(exceedsRemainingTemporary);
  const hasInvalid = normalInvalid || gimmickInvalid;

  const submitReturn = async () => {
    setError("");

    if (!hasAnyReturn) {
      setError("At least one item must be returned.");
      return;
    }

    if (hasInvalid) {
      setError("Fix invalid rows before submitting.");
      return;
    }

    const payloadItems = items
      .filter((it) => {
        if (it.is_temporary) {
          if (tempMode === "resolve") {
            return !exceedsRemainingTemporary(it) && it.remaining > 0 && it.returned_to_stock !== "";
          }
          return toInt(it.returned_to_stock) + toInt(it.permanently_out) > 0;
        }
        return toInt(it.good_qty) + toInt(it.damaged_qty) > 0;
      })
      .map((it) => {
        if (it.is_temporary) {
          return {
            batch_item_id: it.batch_item_id,
            returned_to_stock: toInt(it.returned_to_stock),
            permanently_out:
              tempMode === "partial" ? toInt(it.permanently_out) : undefined,
          };
        }

        return {
          batch_item_id: it.batch_item_id,
          good_qty: toInt(it.good_qty),
          damaged_qty: toInt(it.damaged_qty),
          damaged_note: it.damaged_note,
        };
      });

    setSubmitting(true);

    try {
      await api.post(`/admin/borrow-batches/${batchId}/return/`, {
        temp_mode: tempMode,
        items: payloadItems,
      });

      navigate("/active-borrows");
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.detail ||
        err.response?.data?.error ||
        err.response?.data?.items?.[0] ||
        "Failed to submit return."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!batch) return <div>Batch not found</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-semibold mb-6">
        Return Items — {batch.reference_code}
      </h1>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {typeof error === "string" ? error : JSON.stringify(error)}
        </div>
      )}

      <div className="mb-6 space-y-10">
        <div>
          <h2 className="text-lg font-semibold mb-4">Normal Items</h2>

          {normalItems.length === 0 ? (
            <div className="p-4 border rounded bg-gray-50 text-sm text-gray-500">
              No normal items in this batch.
            </div>
          ) : (
            <table className="w-full border text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 border">Item</th>
                  <th className="p-2 border">Borrowed</th>
                  <th className="p-2 border">Returned</th>
                  <th className="p-2 border">Remaining</th>
                  <th className="p-2 border">Good</th>
                  <th className="p-2 border">Damaged</th>
                  <th className="p-2 border">Note</th>
                </tr>
              </thead>
              <tbody>
                {normalItems.map((it) => {
                  const realIndex = items.findIndex(
                    (row) => row.batch_item_id === it.batch_item_id
                  );
                  const disabled = it.remaining === 0;

                  return (
                    <FragmentLike key={it.batch_item_id}>
                      <tr>
                        <td className="p-2 border">
                          {it.item_code} — {it.item_name}
                        </td>
                        <td className="p-2 border text-center">{it.borrowed}</td>
                        <td className="p-2 border text-center">{it.returned}</td>
                        <td className="p-2 border text-center font-semibold">
                          {it.remaining}
                        </td>
                        <td className="p-2 border">
                          <input
                            type="number"
                            value={it.good_qty}
                            disabled={disabled}
                            onChange={(e) =>
                              updateItem(realIndex, "good_qty", e.target.value)
                            }
                            className={`w-20 border rounded px-2 py-1 ${exceedsRemainingNormal(it) ? "border-red-500" : ""
                              }`}
                          />
                        </td>
                        <td className="p-2 border">
                          <input
                            type="number"
                            value={it.damaged_qty}
                            disabled={disabled}
                            onChange={(e) =>
                              updateItem(realIndex, "damaged_qty", e.target.value)
                            }
                            className={`w-20 border rounded px-2 py-1 ${exceedsRemainingNormal(it) ? "border-red-500" : ""
                              }`}
                          />
                        </td>
                        <td className="p-2 border">
                          <input
                            type="text"
                            value={it.damaged_note}
                            disabled={disabled}
                            onChange={(e) =>
                              updateItem(realIndex, "damaged_note", e.target.value)
                            }
                            className={`w-full border rounded px-2 py-1 ${missingDamagedNote(it) ? "border-red-500" : ""
                              }`}
                            placeholder="Required if damaged"
                          />
                        </td>
                      </tr>

                      {isNormalRowInvalid(it) && (
                        <tr>
                          <td colSpan={7} className="px-2 py-1 text-sm text-red-600">
                            {normalRowErrorMessage(it)}
                          </td>
                        </tr>
                      )}
                    </FragmentLike>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div>
          <h2 className="text-lg font-semibold mb-4">Temporary Items</h2>

          {tempItems.length > 0 && (
            <label className="mb-4 flex items-center gap-3 text-sm font-medium text-strong">
              <input
                type="checkbox"
                checked={tempMode === "partial"}
                onChange={(e) =>
                  setTempMode(e.target.checked ? "partial" : "resolve")
                }
                className="h-4 w-4 rounded border-white/50 dark:border-white/15"
              />
              Partial return
            </label>
          )}

          {tempItems.length === 0 ? (
            <div className="p-4 border rounded bg-gray-50 text-sm text-gray-500">
              No temporary items in this batch.
            </div>
          ) : (
            <table className="w-full border text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 border text-left">Item</th>
                  <th className="p-2 border text-center w-28">Borrowed</th>
                  <th className="p-2 border text-center w-28">Returned</th>
                  <th className="p-2 border text-center w-28">Remaining</th>
                  <th className="p-2 border text-center w-44">Returned to Stock</th>
                  <th className="p-2 border text-center w-44">Permanently Out</th>
                </tr>
              </thead>
              <tbody>
                {tempItems.map((it) => {
                  const realIndex = items.findIndex(
                    (row) => row.batch_item_id === it.batch_item_id
                  );
                  const disabled = it.remaining === 0;

                  return (
                    <FragmentLike key={it.batch_item_id}>
                      <tr>
                        <td className="p-2 border">
                          {it.item_code} — {it.item_name}
                        </td>

                        <td className="p-2 border text-center">{it.borrowed}</td>

                        <td className="p-2 border text-center">{it.returned}</td>

                        <td className="p-2 border text-center font-semibold">
                          {it.remaining}
                        </td>

                        <td className="p-2 border text-center">
                          <input
                            type="number"
                            value={it.returned_to_stock}
                            disabled={disabled}
                            onChange={(e) =>
                              updateItem(realIndex, "returned_to_stock", e.target.value)
                            }
                            className={`w-24 text-center border rounded px-2 py-1 ${exceedsRemainingTemporary(it) ? "border-red-500" : ""
                              }`}
                          />
                        </td>

                        <td className="p-2 border text-center">
                          {tempMode === "resolve" ? (
                            <span className="font-semibold">
                              {computedPermanentOut(it)}
                            </span>
                          ) : (
                            <input
                              type="number"
                              value={it.permanently_out}
                              disabled={disabled}
                              onChange={(e) =>
                                updateItem(realIndex, "permanently_out", e.target.value)
                              }
                              className={`w-24 text-center border rounded px-2 py-1 ${exceedsRemainingTemporary(it) ? "border-red-500" : ""
                                }`}
                            />
                          )}
                        </td>
                      </tr>

                      {exceedsRemainingTemporary(it) && (
                        <tr>
                          <td colSpan={6} className="px-2 py-1 text-sm text-red-600">
                            {gimmickRowErrorMessage(it)}
                          </td>
                        </tr>
                      )}
                    </FragmentLike>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 border rounded"
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          onClick={submitReturn}
          disabled={submitting || hasInvalid || !hasAnyReturn}
          className="px-4 py-2 bg-green-600 text-white rounded disabled:opacity-50"
        >
          {submitting ? "Submitting..." : "Submit Return"}
        </button>
      </div>
    </div>
  );
}

function FragmentLike({ children }) {
  return <>{children}</>;
}